import React from "react";
import ReactDOM from "react-dom";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  HttpLink,
  from,
} from "@apollo/client";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";

import "./index.css";

import App from "./App";
import "react-toastify/dist/ReactToastify.css";
import { AUTH_TOKEN } from "./utils";

// Camaroo brand theme — dark blue + gold (matches the main app's tailwind config)
const theme = createTheme({
  palette: {
    primary: { main: "#0A2540", light: "#143A5E", contrastText: "#FFFFFF" },
    secondary: {
      main: "#D89A2F",
      dark: "#B88022",
      light: "#FFF9F0",
      contrastText: "#0A2540",
    },
  },
  typography: {
    fontFamily:
      '"Outfit", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
  },
});

const clearAuthData = () => {
  localStorage.removeItem(AUTH_TOKEN);
};

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors) {
    for (let err of graphQLErrors) {
      const { message } = err;

      if (message === "Your session has expired. Please log in again.") {
        console.error("Session expired:", message);
        clearAuthData();
        window.location.href = "/login";
        return;
      }

      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${err.locations}, Path: ${err.path}`
      );
    }
  }
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem(AUTH_TOKEN);
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const getGraphQLEndpoint = () => {
  if (process.env.REACT_APP_GRAPHQL_URL) {
    return process.env.REACT_APP_GRAPHQL_URL;
  }

  const currentOrigin = window.location.origin;

  if (currentOrigin.includes("camaroo")) {
    const backendOrigin = currentOrigin.replace("admin.", "adminbe.");
    return `https://${backendOrigin.replace(/^https?:\/\//, "")}/graphql`;
  }

  return "http://localhost:5000/graphql";
};

const httpLink = new HttpLink({
  uri: getGraphQLEndpoint(),
});

const link = from([errorLink, authLink, httpLink]);

const client = new ApolloClient({
  link,
  cache: new InMemoryCache({
    addTypename: false,
  }),
});

ReactDOM.render(
  <BrowserRouter>
    <ApolloProvider client={client}>
      <ThemeProvider theme={theme}>
        <App />
      </ThemeProvider>
    </ApolloProvider>
    <ToastContainer />
  </BrowserRouter>,
  document.getElementById("root")
);
